// Ultimate Card Game (UCG) - Streamlined Deck Selector
// A titleless popup: every deck renders as a solid, non-tearable pack slab
// (same chassis language as the cargo bay boosters — see
// js/ui/factionCrestMarkup.js) that the player picks, not cracks open.
// Clicking a deck snaps it into place and makes it glow before the match
// starts. Adding a new deck happens through a dashed "+" empty-deck slot
// that jumps straight into the integrated Deck Builder editor.

import { DECKS_DATA } from '../data/decks.js';
import { loadCustomDecks, getDeckValidity, MAX_CUSTOM_DECKS, addDeckListener } from '../data/customDecks.js';
import { CARDS_DATA } from '../data/cards.js';
import { getFactionCrestMarkup } from './factionCrestMarkup.js';
import { BayBackground } from '../engine/render/bayBackground.js';
import { hubNavigator } from './hubNavigator.js';
import { REEL_3_STYLES_MAP } from '../engine/slotMachine.js';
import { inventory } from '../engine/inventory.js';
import { preloadAllCardThumbnails } from '../data/cardThumbUrl.js';
import { getDisplayName, getMMR, addIdentityListener } from '../net/playerIdentity.js';
import { showLeaderboardModal, getRankTier } from './leaderboardModal.js';
import { inboxManager } from '../net/inboxManager.js';
import { showDataFeedModal } from './dataFeedModal.js';
import {
  ARCHIVE_PERF_FLAGS,
  ARCHIVE_PERF_CLASS_PREFIX,
  MOBILE_HANGAR_FPS,
  ARCHIVE_PERF_STORAGE_KEY,
  ArchiveFrameProbe,
  isArchiveProbeEnabled,
  mountProbeHud,
  parseArchivePerfFlags,
  readArchivePerfFlags,
  serializeArchivePerfFlags,
} from './archivePerf.js';

const SELECT_ANIM_MS = 300;

export class DeckBuilder {
  constructor(game) {
    this.game = game;
    this.modal = document.getElementById('deck-modal');
    this.isOpen = false;
    this._selecting = false;
    this.bayBg = null;
    this._cardCenters = null;
    this._centeredDeckIdx = -1;
    this._archiveMode = 'singleplayer'; // 'singleplayer' | 'multiplayer'
    this._onlineSubMode = 'normal'; // 'normal' | 'blitz' | 'friendly'
    this._selectedDeckKey = null;
    this._selectedCardEl = null;
    this._lastSelectedDeckKey = null;
    this._swipeSelectTimer = null;
    this._scrollSnapRestoreTimer = null;
    this._isProgrammaticScrolling = false;
    this._lastDeckTapKey = null;
    this._lastDeckTapTime = 0;
    this._attentionPulseTimer = null;
    // A/B switches and frame probe (docs/ARCHIVE_MODE_TOGGLE_PERFORMANCE_PLAN_2026-09-20.md,
    // steps 0/1). Empty and inert unless ?probe=archive, ?archiveperf=... or a stored
    // ucg_archive_perf value asks for them.
    this._archivePerfFlags = [];
    this._archiveProbe = null;
    this._lastDecksFingerprint = null;

    if (typeof addDeckListener === 'function') {
      addDeckListener(() => {
        this._lastDecksFingerprint = null;
        if (this.isOpen) {
          this.renderDecksList(true);
        }
      });
    }

    // Mount BayBackground immediately only if the modal is already active (e.g. in tests or direct link),
    // otherwise defer until openModal()/toggleModal() to avoid downloading 1.1 MB of planes on boot.
    const isModalActive = this.modal?.classList?.contains?.('active') || this.modal?.className?.includes?.('active');
    if (isModalActive) {
      this.ensureBayMounted();
      this.bayBg?.resume();
    }

    this.initListeners();
    this.initModeSelector();
    this._initArchivePerf();
    this.renderDecksList();
  }

  // ── Archive performance tooling (opt-in) ─────────────────────────────
  _initArchivePerf() {
    if (typeof window === 'undefined') return;
    let storage = null;
    try { storage = window.localStorage; } catch { storage = null; }
    const search = window.location?.search || '';
    this._archivePerfFlags = readArchivePerfFlags({ search, storage });
    this._applyArchivePerfFlags();

    if (isArchiveProbeEnabled(search)) {
      this._archiveProbe = new ArchiveFrameProbe({ getMeta: () => this._archivePerfMeta() });
      window.__archiveProbe = this._archiveProbe;
      mountProbeHud({
        probe: this._archiveProbe,
        getFlags: () => this._archivePerfFlags.slice(),
        setFlags: (list) => this.setArchivePerfFlags(list),
        getMode: () => this._archiveMode,
        setMode: (mode) => this.setMode(mode),
        setReference: (on) => this._setPerfReference(on),
        toggleMode: () => this.setMode(this._archiveMode === 'multiplayer' ? 'singleplayer' : 'multiplayer'),
      }, document);
    }
  }

  setArchivePerfFlags(list) {
    this._archivePerfFlags = parseArchivePerfFlags(serializeArchivePerfFlags(list || []));
    try {
      window.localStorage?.setItem?.(ARCHIVE_PERF_STORAGE_KEY, serializeArchivePerfFlags(this._archivePerfFlags));
    } catch { /* storage unavailable: flags stay session-only */ }
    this._applyArchivePerfFlags();
    this._syncBayForMode();
  }

  // Classes live on #deck-modal so the switches can also be flipped from a Web
  // Inspector. closeModal() resets className, hence this is re-applied on every open.
  _applyArchivePerfFlags() {
    const list = this.modal?.classList;
    if (!list || typeof list.toggle !== 'function') return;
    for (const flag of ARCHIVE_PERF_FLAGS) {
      list.toggle(ARCHIVE_PERF_CLASS_PREFIX + flag, this._archivePerfFlags.includes(flag));
    }
  }

  // Reference run for the sweep: nothing on screen and no hangar loop, so the frame cadence
  // that remains is what the display and OS deliver for an empty page.
  _setPerfReference(on) {
    this._perfReference = !!on;
    if (this.modal?.style) this.modal.style.visibility = on ? 'hidden' : '';
    if (on) this.bayBg?.pause();
    else this._syncBayForMode();
  }

  _hasArchivePerfFlag(flag) {
    return this._archivePerfFlags.includes(flag);
  }

  _archivePerfMeta() {
    return {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      dpr: window.devicePixelRatio || 1,
      mobile: !this.isDesktop(),
      mode: this._archiveMode,
      flags: serializeArchivePerfFlags(this._archivePerfFlags) || '-',
      bayFps: this.bayBg?._frameDt ? Math.round(1 / this.bayBg._frameDt) : null,
      bayRunning: !!this.bayBg?.isRunning,
    };
  }

  // Keeps the living hangar in the state the platform and the active switches ask for.
  // Phones run the hangar loop at MOBILE_HANGAR_FPS (30) instead of 60: measured on a device
  // it lifts the idle frame rate of the Archive from ~48 to ~54 fps and removes the load that
  // makes it drift down as the phone warms up, and the hangar keeps moving. Desktop is untouched.
  //   fullfps: back to 60 fps on phones (A/B against the default).
  //   cheap:   draw the hangar once, keep only the parallax (mobile only).
  //   nobay:   diagnostic, loop stopped for good (mobile only).
  // pause() only cancels requestAnimationFrame - the canvases keep their last frame - but one
  // frame is drawn first so a hangar that was never rendered is not left blank.
  _syncBayForMode() {
    const bay = this.bayBg;
    if (!bay || !this.isOpen) return;
    const mobile = !this.isDesktop();
    bay.setFrameRate?.(mobile && !this._hasArchivePerfFlag('fullfps') ? MOBILE_HANGAR_FPS : 60);
    bay.setCheapPath?.(mobile && this._hasArchivePerfFlag('cheap'));
    if (this._perfReference) return;   // reference run: the hangar stays paused, see _setPerfReference()
    if (mobile && this._hasArchivePerfFlag('nobay')) {
      bay.update(0.016);
      bay.render(0.016);
      bay.pause();
      return;
    }
    bay.resume();
  }

  ensureBayMounted() {
    if (this.bayBg) return this.bayBg;
    if (typeof document !== 'undefined' && this.modal && typeof this.modal.prepend === 'function') {
      try {
        this.bayBg = new BayBackground();
        this.bayBg.mount(this.modal);
      } catch (err) {
        console.warn('BayBackground failed to mount:', err);
      }
    }
    return this.bayBg;
  }

  // Tears down the hangar background to free 5 decoded 2048x1152 planes and 2 canvases.
  // Recreated transparently on next ensureBayMounted() call when Archive opens.
  releaseBay() {
    if (this.bayBg) {
      try {
        this.bayBg.destroy();
      } catch (err) {
        console.warn('BayBackground failed to destroy cleanly:', err);
      }
      this.bayBg = null;
    }
  }

  launchSingleplayerMatch() {
    const deckKey = this._selectedDeckKey || this.getLastUsedDeckKey() || 'Human';
    if (!deckKey || !this._selectedCardEl || this._selecting) return;
    if (this._selectedCardEl.classList?.contains?.('incomplete')) {
      this.closeModal();
      this.game.deckBuilderModal?.open();
      this.game.deckBuilderModal?.openDeck(deckKey);
      return;
    }
    this.game?.audio?.playUiClick?.();
    if (this.isChallengeActive()) {
      this.selectAndPlay(this._selectedCardEl, () => {
        if (this.game) this.game._challengePlayed = true;
        this.closeModal();
        this.game.startMatch(deckKey);
      });
    } else {
      this.selectAndPlay(this._selectedCardEl, () => {
        this.game.startMatch(deckKey);
      });
    }
  }

  pulseAttentionButtons() {
    let targets = [];
    if (this._archiveMode === 'multiplayer') {
      const modesContainer = document.getElementById('archive-online-modes');
      if (modesContainer) {
        const btns = Array.from(modesContainer.querySelectorAll('.archive-online-btn'));
        targets = btns.filter(b => !b.classList?.contains?.('hidden') && b.style?.display !== 'none');
      }
    } else if (this.isChallengeActive()) {
      const btnChallengeStart = document.getElementById('btn-mode-challenge-start');
      const btnChallengePass = document.getElementById('btn-mode-challenge-pass');
      if (btnChallengeStart && btnChallengeStart.style?.display !== 'none') targets.push(btnChallengeStart);
      if (btnChallengePass && btnChallengePass.style?.display !== 'none') targets.push(btnChallengePass);
    } else {
      const btnSingle = document.getElementById('btn-mode-singleplayer');
      const btnMulti = document.getElementById('btn-mode-multiplayer');
      if (btnSingle && btnSingle.style?.display !== 'none') targets.push(btnSingle);
      if (btnMulti && btnMulti.style?.display !== 'none') targets.push(btnMulti);
    }

    if (targets.length === 0) return;

    // Randomize between standard and alternate mirrored sci-fi jitter variant for organic variety
    const isAltVariant = Math.random() < 0.5;

    targets.forEach(btn => {
      btn.classList?.remove?.('attention-pulse');
      btn.classList?.remove?.('pulse-alt');
      if (typeof btn.offsetWidth === 'number') {
        void btn.offsetWidth;
      }
      btn.classList?.add?.('attention-pulse');
      if (isAltVariant) {
        btn.classList?.add?.('pulse-alt');
      }
    });

    if (this._attentionPulseTimer) {
      clearTimeout(this._attentionPulseTimer);
    }
    this._attentionPulseTimer = setTimeout(() => {
      targets.forEach(btn => {
        btn.classList?.remove?.('attention-pulse');
        btn.classList?.remove?.('pulse-alt');
      });
      this._attentionPulseTimer = null;
    }, 340);
  }

  initModeSelector() {
    const btnSingle = document.getElementById('btn-mode-singleplayer');
    const btnMulti = document.getElementById('btn-mode-multiplayer');

    btnSingle?.addEventListener('click', () => {
      this.launchSingleplayerMatch();
    });

    btnMulti?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      this.setMode('multiplayer');
    });

    const btnChallengeStart = document.getElementById('btn-mode-challenge-start');
    const btnChallengePass = document.getElementById('btn-mode-challenge-pass');

    btnChallengeStart?.addEventListener('click', () => {
      this.launchSingleplayerMatch();
    });

    btnChallengePass?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      if (this.game) {
        this.game.activeDare = null;
        this.game._isChallengeBriefing = false;
        this.game._challengePlayed = false;
      }
      this.updateChallengeButtons();
    });

    // Sub-mode buttons: 1v1 | Blitz | 2v2 | Friendly (+ Friendly Create / Join)
    const modesContainer = document.getElementById('archive-online-modes');
    const btn1v1 = document.getElementById('btn-online-opt-1v1');
    const btnBlitz = document.getElementById('btn-online-opt-blitz');
    const btn2v2 = document.getElementById('btn-online-opt-2v2');
    const btnFriendly = document.getElementById('btn-online-opt-friendly');
    const btnCreate = document.getElementById('btn-online-friendly-create');
    const btnJoin = document.getElementById('btn-online-friendly-join');

    const selectSub = (sub) => {
      this._onlineSubMode = sub;
      btn1v1?.classList.toggle('active', sub === 'normal');
      btnBlitz?.classList.toggle('active', sub === 'blitz');
      btn2v2?.classList.toggle('active', sub === '2v2');
      btnFriendly?.classList.toggle('active', sub === 'friendly');
      this.game?.audio?.playUiClick?.();
    };

    btn1v1?.addEventListener('click', () => {
      this.resetFriendlyMode();
      selectSub('normal');
      const deckKey = this._selectedDeckKey;
      if (deckKey && this._selectedCardEl) {
        if (this._selectedCardEl.classList?.contains?.('incomplete')) {
          this.closeModal();
          this.game.deckBuilderModal?.open();
          this.game.deckBuilderModal?.openDeck(deckKey);
          return;
        }
        this.selectAndPlay(this._selectedCardEl, () => {
          this.game.onlineLobby?.setMode('normal');
          this.game.onlineLobby?.selectDeck(deckKey);
          this.game.onlineLobby?.openModal();
          this.game.onlineLobby?.quickJoin();
        });
      }
    });

    btnBlitz?.addEventListener('click', () => {
      this.resetFriendlyMode();
      selectSub('blitz');
      const deckKey = this._selectedDeckKey;
      if (deckKey && this._selectedCardEl) {
        if (this._selectedCardEl.classList?.contains?.('incomplete')) {
          this.closeModal();
          this.game.deckBuilderModal?.open();
          this.game.deckBuilderModal?.openDeck(deckKey);
          return;
        }
        this.selectAndPlay(this._selectedCardEl, () => {
          this.game.onlineLobby?.setMode('blitz');
          this.game.onlineLobby?.selectDeck(deckKey);
          this.game.onlineLobby?.openModal();
          this.game.onlineLobby?.quickJoin();
        });
      }
    });

    btn2v2?.addEventListener('click', () => {
      this.resetFriendlyMode();
      selectSub('2v2');
      const deckKey = this._selectedDeckKey;
      if (deckKey && this._selectedCardEl) {
        if (this._selectedCardEl.classList?.contains?.('incomplete')) {
          this.closeModal();
          this.game.deckBuilderModal?.open();
          this.game.deckBuilderModal?.openDeck(deckKey);
          return;
        }
        this.selectAndPlay(this._selectedCardEl, () => {
          this.game.onlineLobby?.setMode('2v2');
          this.game.onlineLobby?.selectDeck(deckKey);
          this.game.onlineLobby?.openModal();
          this.game.onlineLobby?.quickJoin();
        });
      }
    });

    btnFriendly?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      this._friendlyModeActive = true;
      this._onlineSubMode = 'friendly';
      modesContainer?.classList.add('friendly-active');
      btnCreate?.classList.remove('hidden');
      if (btnJoin) {
        btnJoin.classList.remove('hidden', 'is-code-input', 'active', 'code-error');
        btnJoin.textContent = 'Join';
      }
      this.updateFriendlyDeckSelection(this._selectedDeckKey || 'Human');
    });

    btnCreate?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      const deckKey = this._selectedDeckKey || 'Human';
      if (this._selectedCardEl?.classList?.contains?.('incomplete')) {
        this.closeModal();
        this.game.deckBuilderModal?.open();
        this.game.deckBuilderModal?.openDeck(deckKey);
        return;
      }
      if (this.game?.onlineLobby) {
        this.closeModal();
        this.game.onlineLobby.selectDeck(deckKey);
        this.game.onlineLobby.openModal();
        this.game.onlineLobby.create();
      }
    });

    let errorTimer = null;
    const triggerFriendlyCodeError = () => {
      this.game?.audio?.playError?.();
      btnJoin?.classList.remove('code-error');
      if (btnJoin) void btnJoin.offsetWidth;
      btnJoin?.classList.add('code-error');
      const innerInput = btnJoin?.querySelector('#archive-friendly-join-input');
      if (innerInput) {
        innerInput.focus();
        innerInput.select();
      }
      clearTimeout(errorTimer);
      errorTimer = setTimeout(() => {
        btnJoin?.classList.remove('code-error');
      }, 1400);
    };

    const triggerFriendlyJoin = (code) => {
      const cleanCode = (code || '').trim().toUpperCase();
      if (!cleanCode || cleanCode.length !== 6) {
        triggerFriendlyCodeError();
        return;
      }
      this.game?.audio?.playUiClick?.();
      const deckKey = this._selectedDeckKey || 'Human';
      if (this._selectedCardEl?.classList?.contains?.('incomplete')) {
        this.closeModal();
        this.game.deckBuilderModal?.open();
        this.game.deckBuilderModal?.openDeck(deckKey);
        return;
      }
      if (this.game?.onlineLobby) {
        this.game.onlineLobby.selectDeck(deckKey);
        this.game.onlineLobby.join(cleanCode, {
          onError: () => {
            triggerFriendlyCodeError();
          },
          onSuccess: () => {
            btnJoin?.classList.remove('code-error');
            this.closeModal();
          }
        });
      }
    };

    btnJoin?.addEventListener('click', (e) => {
      if (btnJoin.classList.contains('is-code-input')) return;
      this.game?.audio?.playUiClick?.();
      btnJoin.classList.add('is-code-input', 'active');
      btnJoin.innerHTML = '<input type="text" id="archive-friendly-join-input" class="archive-join-inline-input" placeholder="CODE" maxlength="6" autocomplete="off" spellcheck="false" />';
      const innerInput = btnJoin.querySelector('#archive-friendly-join-input');
      setTimeout(() => innerInput?.focus(), 50);

      innerInput?.addEventListener('input', () => {
        btnJoin.classList.remove('code-error');
        const val = innerInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        innerInput.value = val;
        if (val.length === 6) {
          triggerFriendlyJoin(val);
        }
      });

      innerInput?.addEventListener('blur', () => {
        if (!innerInput.value.trim() && !btnJoin.classList.contains('code-error')) {
          btnJoin.classList.remove('is-code-input', 'active', 'code-error');
          btnJoin.textContent = 'Join';
        }
      });

      innerInput?.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape') {
          btnJoin.classList.remove('is-code-input', 'active', 'code-error');
          btnJoin.textContent = 'Join';
        } else if (ev.key === 'Enter') {
          const val = innerInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
          if (val.length === 6) {
            triggerFriendlyJoin(val);
          } else {
            triggerFriendlyCodeError();
          }
        }
      });
    });

    // Ladder shortcut button in summary tile
    document.getElementById('btn-archive-online-ladder')?.addEventListener('click', () => {
      this.game?.audio?.playUiClick?.();
      showLeaderboardModal(this.game);
    });

    this.updateOnlineSummary();
    addIdentityListener(() => this.updateOnlineSummary());
  }

  updateFriendlyDeckSelection(deckKey) {
    this._selectedDeckKey = deckKey || 'Human';

    // Highlight the chosen card in the grid
    const container = document.getElementById('deck-presets-list');
    container?.querySelectorAll('.deck-select-card')?.forEach(card => {
      const cardDeckKey = card.dataset?.deckKey ?? card.getAttribute?.('data-deck-key');
      const isSelected = cardDeckKey === this._selectedDeckKey;
      card.classList.toggle('friendly-selected', isSelected);
    });

    // Determine race, first word (max 9 chars), and custom color
    let firstWord = 'Human';
    let isSul = false;
    let customHex = null;

    if (this._selectedDeckKey === 'Sul' || this._selectedDeckKey === 'Sul Swarm') {
      firstWord = 'Sul';
      isSul = true;
    } else if (this._selectedDeckKey === 'Human' || this._selectedDeckKey === 'Human Vanguard') {
      firstWord = 'Human';
      isSul = false;
    } else if (DECKS_DATA[this._selectedDeckKey]) {
      const d = DECKS_DATA[this._selectedDeckKey];
      const rawName = (d.name || this._selectedDeckKey).trim();
      firstWord = rawName.split(/\s+/)[0] || (d.race === 'Sul' ? 'Sul' : 'Human');
      isSul = d.race === 'Sul';
    } else {
      const custom = (loadCustomDecks() || []).find(d => d.id === this._selectedDeckKey);
      if (custom) {
        const rawName = (custom.name || (custom.race === 'Sul' ? 'Sul' : 'Human')).trim();
        firstWord = rawName.split(/\s+/)[0] || (custom.race === 'Sul' ? 'Sul' : 'Human');
        isSul = custom.race === 'Sul';
        if (custom.customColorId && REEL_3_STYLES_MAP?.has(custom.customColorId)) {
          customHex = REEL_3_STYLES_MAP.get(custom.customColorId)?.hex || null;
        }
      }
    }

    if (firstWord.length > 9) {
      firstWord = firstWord.slice(0, 9);
    }

    // Update Friendly button text & color to match selected deck
    const btnFriendly = document.getElementById('btn-online-opt-friendly');
    if (btnFriendly && this._onlineSubMode === 'friendly') {
      btnFriendly.textContent = firstWord;
      btnFriendly.title = `Friendly match (${firstWord})`;
      btnFriendly.classList.toggle('race-sul', isSul);
      btnFriendly.classList.toggle('race-human', !isSul);
      if (customHex) {
        btnFriendly.style.borderColor = customHex;
        btnFriendly.style.boxShadow = `0 0 16px ${customHex}80`;
        btnFriendly.style.background = `linear-gradient(135deg, ${customHex}40 0%, ${customHex}60 100%)`;
      } else {
        btnFriendly.style.borderColor = '';
        btnFriendly.style.boxShadow = '';
        btnFriendly.style.background = '';
      }
    }

    // Hint pill remains hidden (replaced by direct Friendly button coloring & race text)
    const pill = document.getElementById('archive-friendly-deck-pill');
    const nameEl = document.getElementById('archive-friendly-deck-name');
    if (nameEl) nameEl.textContent = firstWord;
    if (pill) pill.classList.add('hidden');
  }

  resetFriendlyMode() {
    this._friendlyModeActive = false;
    this._friendlyRoomCode = null;
    const modesContainer = document.getElementById('archive-online-modes');
    modesContainer?.classList.remove('friendly-active');
    const btnCreate = document.getElementById('btn-online-friendly-create');
    const btnJoin = document.getElementById('btn-online-friendly-join');
    if (btnCreate) {
      btnCreate.classList.add('hidden');
      btnCreate.classList.remove('active');
      btnCreate.textContent = 'Host';
    }
    if (btnJoin) {
      btnJoin.classList.add('hidden');
      btnJoin.classList.remove('is-code-input', 'active', 'code-error');
      btnJoin.textContent = 'Join';
    }
    const btnFriendly = document.getElementById('btn-online-opt-friendly');
    if (btnFriendly) {
      btnFriendly.classList.remove('active', 'race-sul', 'race-human');
      btnFriendly.textContent = 'Friendly';
      btnFriendly.title = 'Friendly match';
      btnFriendly.style.borderColor = '';
      btnFriendly.style.boxShadow = '';
      btnFriendly.style.background = '';
    }
    const pill = document.getElementById('archive-friendly-deck-pill');
    if (pill) pill.classList.add('hidden');
    const container = document.getElementById('deck-presets-list');
    container?.querySelectorAll?.('.deck-select-card.friendly-selected')?.forEach(c => c.classList.remove('friendly-selected'));
  }

  isChallengeActive() {
    return Boolean(this.game?.activeDare && !this.game?._challengePlayed && !this.game?.isRunning);
  }

  updateChallengeButtons() {
    const isChallenge = this.isChallengeActive();
    const btnSingle = document.getElementById('btn-mode-singleplayer');
    const btnMulti = document.getElementById('btn-mode-multiplayer');
    const btnChallengeStart = document.getElementById('btn-mode-challenge-start');
    const btnChallengePass = document.getElementById('btn-mode-challenge-pass');

    if (btnSingle?.style) btnSingle.style.display = isChallenge ? 'none' : '';
    if (btnMulti?.style) btnMulti.style.display = isChallenge ? 'none' : '';
    if (btnChallengeStart?.style) btnChallengeStart.style.display = isChallenge ? '' : 'none';
    if (btnChallengePass?.style) btnChallengePass.style.display = isChallenge ? '' : 'none';
  }

  getLastUsedDeckKey() {
    if (this._lastSelectedDeckKey) return this._lastSelectedDeckKey;
    try {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('ucg_last_deck_id');
        if (saved) return saved;
      }
    } catch (e) {}
    const currentId = this.game?.currentPlayerDeckId;
    if (currentId && currentId !== 'Standard') {
      return currentId;
    }
    return 'Human';
  }

  scrollToCard(cardEl, smooth = true) {
    const container = document.getElementById('deck-presets-list');
    if (!container || !cardEl) return;

    const cards = typeof container.querySelectorAll === 'function'
      ? Array.from(container.querySelectorAll('.deck-select-card'))
      : (container.children || []);
    const cardIdx = cards.indexOf(cardEl);
    if (cardIdx >= 0) {
      this._centeredDeckIdx = cardIdx;
      this._applyCenteredCardClass();
    }

    if (typeof container.getBoundingClientRect === 'function' && typeof cardEl.getBoundingClientRect === 'function') {
      const cBox = container.getBoundingClientRect();
      const cardBox = cardEl.getBoundingClientRect();
      if (cBox.width > 0 && cardBox.width > 0) {
        const delta = (cardBox.left + cardBox.width / 2) - (cBox.left + cBox.width / 2);
        if (Math.abs(delta) > 1) {
          const targetScrollLeft = Math.max(0, container.scrollLeft + delta);
          this._isProgrammaticScrolling = true;
          if (smooth && typeof container.scrollTo === 'function') {
            container.scrollTo({
              left: targetScrollLeft,
              behavior: 'smooth'
            });
            if (this._scrollSnapRestoreTimer) clearTimeout(this._scrollSnapRestoreTimer);
            this._scrollSnapRestoreTimer = setTimeout(() => {
              this._isProgrammaticScrolling = false;
              this.updateScrollProgress();
              this.checkDeckSwipeChange();
            }, 260);
          } else {
            container.scrollLeft = targetScrollLeft;
            this._isProgrammaticScrolling = false;
            this.updateScrollProgress();
            this.checkDeckSwipeChange();
          }
        }
        return;
      }
    }

    // Fallback for headless / mock DOM
    const cardCenter = (cardEl.offsetLeft || 0) + (cardEl.offsetWidth || 0) / 2;
    const containerWidth = container.clientWidth || 0;
    if (containerWidth > 0) {
      container.scrollLeft = Math.max(0, cardCenter - containerWidth / 2);
    }
    if (typeof cardEl.scrollIntoView === 'function') {
      cardEl.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', inline: 'center', block: 'nearest' });
    }
  }

  selectDeck(deckKey, cardEl, opts = {}) {
    if (this._selecting) return;

    const now = Date.now();
    const isUserTap = !opts.fromSwipe && !opts.silent;
    const isDoubleTap = isUserTap &&
      this._lastDeckTapKey === deckKey &&
      this._lastDeckTapTime &&
      (now - this._lastDeckTapTime < 400);

    if (isUserTap) {
      this._lastDeckTapKey = deckKey;
      this._lastDeckTapTime = now;
    }

    if (isDoubleTap) {
      this._lastDeckTapTime = 0;
      this.launchSingleplayerMatch();
      return;
    }

    if (this._selectedDeckKey === deckKey) {
      if (!opts.silent && !opts.fromSwipe) {
        this.game?.audio?.playUiClick?.({ rate: 1.25 });
        this.pulseAttentionButtons();
      }
      // On mobile: ensure centered
      if (!this.isDesktop() && !opts.fromSwipe && cardEl) {
        this.scrollToCard(cardEl, true);
      }
      return;
    }
    this._selectedDeckKey = deckKey;
    this._selectedCardEl = cardEl;
    if (deckKey) {
      this._lastSelectedDeckKey = deckKey;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('ucg_last_deck_id', deckKey);
        }
      } catch (e) {}
    }

    this.modal?.querySelectorAll?.('.deck-select-card.selected')?.forEach(c => c.classList?.remove?.('selected'));
    cardEl?.classList?.add?.('selected');
    this.modal?.classList?.add?.('has-deck-selected');

    this.resetFriendlyMode();
    this.setMode('singleplayer');
    const btnSingle = document.getElementById('btn-mode-singleplayer');
    const btnMulti = document.getElementById('btn-mode-multiplayer');
    btnSingle?.classList?.remove?.('active');
    btnMulti?.classList?.remove?.('active');
    this.updateChallengeButtons();

    if (!opts.silent && !opts.fromSwipe) {
      this.game?.audio?.playUiClick?.();
    }

    if (!this.isDesktop() && !opts.fromSwipe && cardEl) {
      this.scrollToCard(cardEl, !opts.instant);
    }
  }

  _initMobileDeckSelection() {
    if (this.isDesktop() || !this.isOpen) return;
    const container = document.getElementById('deck-presets-list');
    if (!container) return;

    const allCards = typeof container.querySelectorAll === 'function'
      ? Array.from(container.querySelectorAll('.deck-select-card'))
      : (container.children || []);
    const cards = allCards.filter(c => !c.classList?.contains?.('deck-add-card') && !c.classList?.contains?.('incomplete'));
    if (cards.length === 0) return;

    const targetKey = this.getLastUsedDeckKey();
    let targetCard = cards.find(c => c.dataset?.deckKey === targetKey);
    if (!targetCard) {
      targetCard = cards[0];
    }
    const deckKey = targetCard?.dataset?.deckKey || 'Human';

    // Direct selection without click audio, instant centering without animation
    this.selectDeck(deckKey, targetCard, { silent: true, instant: true });

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        if (this.isOpen && !this.isDesktop() && this._selectedCardEl) {
          this.scrollToCard(this._selectedCardEl, false);
        }
      });
    }
  }

  _restoreDesktopDeckSelection() {
    if (!this.isDesktop() || !this.isOpen || !this._lastSelectedDeckKey) return;
    const container = document.getElementById('deck-presets-list');
    if (!container) return;

    const allCards = typeof container.querySelectorAll === 'function'
      ? Array.from(container.querySelectorAll('.deck-select-card'))
      : (container.children || []);
    const targetCard = allCards.find(c => (c.dataset?.deckKey || c.getAttribute?.('data-deck-key')) === this._lastSelectedDeckKey);
    if (targetCard && !targetCard.classList?.contains?.('incomplete') && !targetCard.classList?.contains?.('deck-add-card')) {
      this.selectDeck(this._lastSelectedDeckKey, targetCard, { silent: true, instant: true });
    } else {
      this._lastSelectedDeckKey = null;
    }
  }

  clearDeckSelection(opts = {}) {
    if (opts?.clearLast) {
      this._lastSelectedDeckKey = null;
    }
    this._selectedDeckKey = null;
    this._selectedCardEl = null;
    this._lastDeckTapKey = null;
    this._lastDeckTapTime = 0;
    this.modal?.classList?.remove?.('has-deck-selected');
    this.modal?.querySelectorAll?.('.deck-select-card.selected')?.forEach(c => c.classList?.remove?.('selected'));
    this.resetFriendlyMode();
    this.setMode('singleplayer');
    const btnSingle = document.getElementById('btn-mode-singleplayer');
    const btnMulti = document.getElementById('btn-mode-multiplayer');
    btnSingle?.classList?.remove?.('active');
    btnMulti?.classList?.remove?.('active');
    this.updateChallengeButtons();
  }

  updateOnlineSummary() {
    const nameEl = document.getElementById('archive-online-name');
    const rankEl = document.getElementById('archive-online-rank');
    if (!nameEl && !rankEl) return;
    const name = getDisplayName() || 'Commander';
    const mmr = getMMR() || 1500;
    const tier = getRankTier(mmr);
    if (nameEl) nameEl.textContent = name;
    if (rankEl) rankEl.innerHTML = `${tier.icon} ${mmr} MMR`;
  }

  setMode(mode) {
    const opts = arguments[1] || {};
    const targetMode = mode === 'multiplayer' ? 'multiplayer' : 'singleplayer';
    if (this._archiveMode === targetMode) return;
    const previousMode = this._archiveMode;
    this._archiveMode = targetMode;
    const btnSingle = document.getElementById('btn-mode-singleplayer');
    const btnMulti = document.getElementById('btn-mode-multiplayer');
    btnSingle?.classList.toggle('active', this._archiveMode === 'singleplayer');
    btnMulti?.classList.toggle('active', this._archiveMode === 'multiplayer');

    const deckModal = document.getElementById('deck-modal');
    if (deckModal) {
      deckModal.classList.toggle('archive-mode-multiplayer', this._archiveMode === 'multiplayer');
    }
    this._archiveProbe?.begin(`${previousMode === 'multiplayer' ? 'MP' : 'SP'}->${targetMode === 'multiplayer' ? 'MP' : 'SP'}`, { auto: true });
    if (this._archiveMode === 'multiplayer') {
      if (typeof window !== 'undefined' && window.history?.pushState && !opts?.fromHistory) {
        try {
          if (window.history.state?.screen !== 'archive_multiplayer') {
            window.history.pushState({ screen: 'archive_multiplayer' }, '');
          }
        } catch (e) {}
      }
      this.updateOnlineSummary();
      if (this._onlineSubMode === 'friendly') {
        this.updateFriendlyDeckSelection(this._selectedDeckKey || 'Human');
      }
    } else {
      if (typeof window !== 'undefined' && window.history?.replaceState && !opts?.fromHistory) {
        try {
          if (window.history.state?.screen === 'archive_multiplayer') {
            window.history.replaceState({ screen: 'archive' }, '');
          }
        } catch (e) {}
      }
      this.resetFriendlyMode();
    }
  }

  initListeners() {
    const btnOpen = document.getElementById('btn-open-decks');
    const btnQuickDecks = document.getElementById('btn-quick-decks');
    const btnClose = document.getElementById('btn-close-decks');
    if (btnOpen) btnOpen.addEventListener('click', () => this.toggleModal());
    if (btnQuickDecks) btnQuickDecks.addEventListener('click', () => this.open());
    if (btnClose) btnClose.addEventListener('click', () => this.closeModal());

    // Clicking on background / empty space deselects the chosen deck
    this.modal?.addEventListener('click', (e) => {
      if (!this.isOpen || this._selecting) return;
      if (e.target.closest?.('.deck-select-card') || e.target.closest?.('.archive-mode-toggle-bar') || e.target.closest?.('.deckbuilder-header') || e.target.closest?.('#archive-exit-gear') || e.target.closest?.('.archive-exit-gear') || e.target.closest?.('#archive-data-indicator')) {
        return;
      }
      if (this._selectedDeckKey || this._lastSelectedDeckKey) {
        if (!this.isDesktop()) return; // On mobile, a deck always stays selected in the carousel
        this.clearDeckSelection({ clearLast: true });
        this.game?.audio?.playUiClick?.();
      }
    });

    // Singleplayer-Kampagne (docs/game-design-singleplayer-kampagne.md):
    // no real menu entry yet while only the Mission 1 vertical slice
    // exists - see the invisible #secret-campaign-hotspot in index.html.
    // Desktop only: on mobile the demo should not be triggerable.
    const btnCampaign = document.getElementById('secret-campaign-hotspot');
    if (btnCampaign) {
      btnCampaign.addEventListener('click', (e) => {
        if (!this.isDesktop()) {
          e?.preventDefault?.();
          e?.stopPropagation?.();
          return;
        }
        this.closeModal();
        this.game.startCampaignMission('m1_absprung');
      });
    }

    // Archive Exit / Gear button: Return to Startscreen (Hauptmenü)
    const exitGear = document.getElementById('archive-exit-gear');
    if (exitGear) {
      exitGear.addEventListener('click', (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        this.game?.audio?.playUiClick?.();
        if (hubNavigator?.playTransitBeep) {
          hubNavigator.playTransitBeep('exit');
        }
        this.closeModal();
        hubNavigator.exitStation();
        if (this.game?.startScreen?.show) {
          this.game.startScreen.show();
        }
      });
    }

    // Archive Sub-space Data Indicator: opens Community Transmissions / Data Feed
    const dataIndicator = document.getElementById('archive-data-indicator');
    if (dataIndicator) {
      dataIndicator.addEventListener('click', (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        this.game?.audio?.playUiClick?.();
        showDataFeedModal(this.game);
      });
    }

    inboxManager.addListener(() => {
      this.updateDataIndicator();
    });

    // Escape Key Handler: Return to Startscreen (Hauptmenü)
    document.addEventListener?.('keydown', (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (!this.isOpen) return;
        if (this.game?.directivesModal?.isOpen?.()) return;
        if (this.game?.deckBuilderModal?.isOpen?.()) return;
        e.stopPropagation();
        this.closeModal();
        if (!this.game?.isRunning || this.game?.isGameOver) {
          this.game?.startScreen?.show();
        }
      }
    });

    // Android Hardware Back Button / Edge-Swipe Gesture Handler (popstate)
    if (typeof window !== 'undefined') {
      window.addEventListener?.('popstate', (e) => {
        if (!this.isOpen) {
          // If another hub deck (Cargo Bay, Supply, Arsenal) is open, exit station to start screen
          if (hubNavigator.activeDeck && hubNavigator.activeDeck !== 'archive') {
            hubNavigator.exitStation();
          }
          return;
        }

        // 1. If an inner modal (Directives, Deck Editor) is open, let it close first
        if (this.game?.directivesModal?.isOpen?.()) {
          this.game.directivesModal.close();
          return;
        }
        if (this.game?.deckBuilderModal?.isOpen?.()) {
          this.game.deckBuilderModal.close();
          return;
        }

        // 2. If inside multiplayer mode / tray, step back to singleplayer mode first
        if (this._archiveMode === 'multiplayer') {
          this.resetFriendlyMode();
          this.setMode('singleplayer', { fromHistory: true });
          return;
        }

        // 3. In base archive screen: close archive and return to start screen
        this.closeModal({ fromHistory: true });
        if (!this.game?.isRunning || this.game?.isGameOver) {
          this.game?.startScreen?.show();
        }
      });
    }

    // Number keys 1-5 pick the deck at that position (presets first, then
    // custom decks, in the same order they're rendered in renderDecksList()).
    document.addEventListener?.('keydown', (e) => {
      if (!this.isOpen || this._selecting) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= 5) {
        const cards = this.modal?.querySelectorAll('.deck-select-card:not(.deck-add-card)');
        const card = cards?.[num - 1];
        if (card) {
          e.preventDefault();
          card.click();
        }
      }
    });

    // Mobile Touch / Horizontal Swipe synchronization with Background Parallax & Audio Feedback
    const container = document.getElementById('deck-presets-list');
    if (container && typeof container.addEventListener === 'function') {
      let isScrollTicking = false;
      const onScrollUpdate = () => {
        this.updateScrollProgress();
        if (!this.isDesktop()) {
          this.checkDeckSwipeChange();
        }
      };
      container.addEventListener('scroll', () => {
        if (!isScrollTicking) {
          isScrollTicking = true;
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => {
              isScrollTicking = false;
              onScrollUpdate();
            });
          } else {
            isScrollTicking = false;
            onScrollUpdate();
          }
        }
      }, { passive: true });

      if (typeof container.addEventListener === 'function') {
        container.addEventListener('scrollend', () => {
          if (!this.isDesktop()) {
            this.checkDeckSwipeChange();
            this._applySettledSwipeSelection();
          }
        }, { passive: true });
        container.addEventListener('touchend', () => {
          if (!this.isDesktop()) {
            this.checkDeckSwipeChange();
            this._applySettledSwipeSelection();
          }
        }, { passive: true });
        container.addEventListener('touchstart', () => {
          if (!this.isDesktop()) {
            this._isProgrammaticScrolling = false;
            if (this._scrollSnapRestoreTimer) {
              clearTimeout(this._scrollSnapRestoreTimer);
              this._scrollSnapRestoreTimer = null;
            }
          }
        }, { passive: true });
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener?.('resize', () => {
        this._cardCenters = null;
        this.updateScrollProgress();
      }, { passive: true });
    }
  }

  updateScrollProgress() {
    const container = document.getElementById('deck-presets-list');
    if (!container) return;
    const maxScroll = (container.scrollWidth || 0) - (container.clientWidth || 0);
    if (maxScroll > 0) {
      const progress = Math.max(0, Math.min(1, (container.scrollLeft || 0) / maxScroll));
      const norm = (progress - 0.5) * 2;
      this.bayBg?.setScrollProgress?.(progress);
      this.modal?.style?.setProperty?.('--deck-scroll-norm', norm.toFixed(4));
    } else {
      this.bayBg?.setScrollProgress?.(0.5);
      this.modal?.style?.setProperty?.('--deck-scroll-norm', '0');
    }
  }

  getCenteredDeckIndex() {
    const container = document.getElementById('deck-presets-list');
    if (!container) return -1;
    const cards = typeof container.querySelectorAll === 'function'
      ? Array.from(container.querySelectorAll('.deck-select-card'))
      : (container.children || []);
    if (!cards || cards.length === 0) return -1;

    const firstCard = cards[0];
    if (typeof container.getBoundingClientRect === 'function' && typeof firstCard.getBoundingClientRect === 'function') {
      const cBox = container.getBoundingClientRect();
      if (cBox.width > 0) {
        const containerCenter = cBox.left + cBox.width / 2;
        let closestIdx = 0;
        let closestDist = Infinity;
        for (let i = 0; i < cards.length; i++) {
          const card = cards[i];
          const box = card.getBoundingClientRect();
          const cardCenter = box.left + box.width / 2;
          const dist = Math.abs(cardCenter - containerCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        }
        return closestIdx;
      }
    }

    // Fallback for headless / mock DOM without layout geometry
    const maxScroll = (container.scrollWidth || 0) - (container.clientWidth || 0);
    if (maxScroll > 0 && cards.length > 1) {
      const progress = Math.max(0, Math.min(1, (container.scrollLeft || 0) / maxScroll));
      return Math.min(cards.length - 1, Math.round(progress * (cards.length - 1)));
    }
    return 0;
  }

  checkDeckSwipeChange() {
    if (this._isProgrammaticScrolling || this.isDesktop() || !this.isOpen) {
      if (this.isDesktop() && this._centeredDeckIdx !== -1) {
        this._centeredDeckIdx = -1;
        this._applyCenteredCardClass();
      }
      return;
    }

    const newIdx = this.getCenteredDeckIndex();
    if (newIdx >= 0 && this._centeredDeckIdx !== -1 && newIdx !== this._centeredDeckIdx) {
      this._centeredDeckIdx = newIdx;
      this._applyCenteredCardClass();
      this.game?.audio?.playDeckHover?.();
      this._syncMobileSelectionForCenteredDeck(newIdx);
    } else if (this._centeredDeckIdx === -1 && newIdx >= 0) {
      this._centeredDeckIdx = newIdx;
      this._applyCenteredCardClass();
      this._syncMobileSelectionForCenteredDeck(newIdx);
    }
  }

  _syncMobileSelectionForCenteredDeck(idx) {
    if (this.isDesktop() || !this.isOpen) return;
    const container = document.getElementById('deck-presets-list');
    if (!container) return;
    const cards = typeof container.querySelectorAll === 'function'
      ? Array.from(container.querySelectorAll('.deck-select-card'))
      : (container.children || []);
    if (idx < 0 || idx >= cards.length) return;
    const card = cards[idx];
    if (!card) return;

    // If "+ New Deck" or incomplete custom deck is centered, clear selection so mode buttons hide
    if (card.classList?.contains?.('deck-add-card') || card.classList?.contains?.('incomplete') || !card.dataset?.deckKey) {
      this.clearDeckSelection();
      return;
    }

    const deckKey = card.dataset.deckKey;
    if (this._selectedDeckKey !== deckKey) {
      this.selectDeck(deckKey, card, { fromSwipe: true, silent: true });
    }
  }

  _applySettledSwipeSelection() {
    if (this._isProgrammaticScrolling || this.isDesktop() || !this.isOpen) return;
    this._syncMobileSelectionForCenteredDeck(this.getCenteredDeckIndex());
  }

  _applyCenteredCardClass() {
    const container = document.getElementById('deck-presets-list');
    if (!container || typeof container.querySelectorAll !== 'function') return;
    const cards = container.querySelectorAll('.deck-select-card');
    cards.forEach((card, i) => {
      if (typeof card.classList?.toggle === 'function') {
        card.classList.toggle('centered', i === this._centeredDeckIdx);
      } else if (card.classList) {
        if (i === this._centeredDeckIdx) card.classList.add('centered');
        else card.classList.remove('centered');
      }
    });
  }

  _getDecksFingerprint() {
    const customDecks = loadCustomDecks();
    const equippedColor = inventory?.getEquippedDeckColor ? inventory.getEquippedDeckColor() : '';
    let fp = `c:${equippedColor}|`;
    for (let i = 0; i < customDecks.length; i++) {
      const cd = customDecks[i];
      fp += `${cd.id}:${cd.name}:${cd.race}:${cd.icon || ''}:${cd.color || ''}:${cd.common?.length || 0}:${cd.uncommon?.length || 0}:${cd.epic?.length || 0}|`;
      if (cd.cards && typeof cd.cards === 'object') {
        fp += Object.keys(cd.cards).length + '|';
      }
    }
    return fp;
  }

  renderDecksList(forceRebuild = false) {
    this._cardCenters = null;
    const container = document.getElementById('deck-presets-list');
    if (!container) return;

    const currentFp = this._getDecksFingerprint();
    const cards = container.querySelectorAll ? Array.from(container.querySelectorAll('.deck-select-card')) : (container.children || []);
    if (!forceRebuild && cards.length > 0 && this._lastDecksFingerprint === currentFp) {
      for (let i = 0; i < cards.length; i++) {
        const item = cards[i];
        const key = item.dataset?.deckKey || (item.getAttribute && item.getAttribute('data-deck-key'));
        if (key && item.classList) {
          const isFriendlySelected = (this._archiveMode === 'multiplayer' && this._onlineSubMode === 'friendly' && key === this._selectedDeckKey);
          if (item.classList.toggle) {
            item.classList.toggle('friendly-selected', isFriendlySelected);
          }
        }
      }
      this.updateScrollProgress();
      this._centeredDeckIdx = this.getCenteredDeckIndex();
      this._applyCenteredCardClass();
      return;
    }

    this._lastDecksFingerprint = currentFp;
    container.innerHTML = '';
    let crestIdx = 0;

    // 1. Presets (Human Vanguard, Sul Swarm) — each gets the same crest
    // logo already used on its matching cargo bay booster pack.
    Object.keys(DECKS_DATA).forEach(deckKey => {
      const d = DECKS_DATA[deckKey];
      const totalCards = (d.common?.length || 0) + (d.uncommon?.length || 0) + (d.epic?.length || 0);
      const raceClass = d.race === 'Sul' ? 'race-sul' : 'race-human';
      const crestMarkup = getFactionCrestMarkup(d.race, `-deckstd-${crestIdx++}`);

      const item = document.createElement('div');
      item.className = `deck-select-card standard ${raceClass}`;
      if (!item.dataset) item.dataset = {};
      item.dataset.deckKey = deckKey;
      if (item.setAttribute) item.setAttribute('data-deck-key', deckKey);
      if (this._archiveMode === 'multiplayer' && this._onlineSubMode === 'friendly' && deckKey === this._selectedDeckKey) {
        item.classList.add('friendly-selected');
      }
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.title = `Select ${d.name}`;
      item.innerHTML = `
        <div class="deck-card-slab">
          <div class="deck-slab-depth">
            <div class="deck-chassis-back"></div>
            <div class="deck-chassis-slice"></div>
            <div class="deck-side deck-side-l"></div>
            <div class="deck-side deck-side-r"></div>
            <div class="deck-side deck-side-t"></div>
            <div class="deck-side deck-side-b"></div>
          </div>
          <div class="deck-card-face">
            <div class="deck-plate-pattern"></div>
            <div class="deck-card-bezel"></div>
            <div class="deck-card-crest">${crestMarkup}</div>
            <div class="deck-card-info">
              <div class="deck-card-name">${d.name}</div>
              <div class="deck-card-meta">${totalCards} Cards</div>
            </div>
            <div class="deck-front-glass"></div>
          </div>
        </div>
      `;
      this.setupCardTilt(item);
      const handleSelect = () => {
        this.selectDeck(deckKey, item);
      };
      item.addEventListener('click', handleSelect);
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      });
      container.appendChild(item);
    });

    // 2. Player-Created Custom Decks — a simpler generic emblem (no crest;
    // that stays reserved for the official standard decks), plus a small
    // edit affordance to jump into the deck editor.
    const customDecks = loadCustomDecks();
    customDecks.forEach(deck => {
      let totalCards = (deck.common?.length || 0) + (deck.uncommon?.length || 0) + (deck.epic?.length || 0);
      let cardsMap = deck.cards && typeof deck.cards === 'object' ? deck.cards : null;
      if (totalCards === 0 && cardsMap) {
        totalCards = Object.values(cardsMap).reduce((sum, n) => sum + (Number(n) || 0), 0);
      }

      // The deck builder autosaves on every edit (see
      // DeckBuilderModal.autosaveDeck()), so a deck sitting in storage no
      // longer implies it's finished - it may still be short cards or be an empty draft.
      // editor's own Save button enforces, so an incomplete draft is
      // visible (nothing lost) but can't actually be picked to start a
      // match with; tapping it opens the editor instead, same as the
      // pencil button.
      if (!cardsMap) {
        cardsMap = {};
        for (const name of deck.common || []) cardsMap[name] = (cardsMap[name] || 0) + 1;
        for (const name of deck.uncommon || []) cardsMap[name] = (cardsMap[name] || 0) + 1;
        for (const name of deck.epic || []) cardsMap[name] = (cardsMap[name] || 0) + 1;
      }
      const { isValid } = getDeckValidity(cardsMap, CARDS_DATA, deck.race);
      const isIncomplete = !isValid;

      const raceClass = deck.race === 'Sul' ? 'race-sul' : 'race-human';

      const customColorId = deck.color || inventory.getEquippedDeckColor();
      const colorStyle = customColorId ? REEL_3_STYLES_MAP.get(customColorId) : null;
      const customColorClass = colorStyle ? 'has-custom-color' : '';

      let crestHtml = '';
      const iconId = deck.icon || 'generic-a';
      crestHtml = `<div class="deck-card-crest">${getFactionCrestMarkup(iconId, `-deckcustom-${crestIdx++}`)}</div>`;

      const item = document.createElement('div');
      item.className = `deck-select-card custom ${raceClass} ${customColorClass} ${isIncomplete ? 'incomplete' : ''}`;
      if (!item.dataset) item.dataset = {};
      item.dataset.deckKey = deck.id;
      if (item.setAttribute) item.setAttribute('data-deck-key', deck.id);
      if (this._archiveMode === 'multiplayer' && this._onlineSubMode === 'friendly' && deck.id === this._selectedDeckKey) {
        item.classList.add('friendly-selected');
      }
      if (colorStyle) {
        item.style.setProperty('--deck-accent', colorStyle.hex);
        item.style.setProperty('--deck-accent-glow', `${colorStyle.hex}cc`);
        item.style.setProperty('--deck-accent-core', `${colorStyle.hex}b3`);
      }
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.title = isIncomplete
        ? `${deck.name} — Incomplete (Click to edit)`
        : `Select ${deck.name}`;
      item.innerHTML = `
        <div class="deck-card-slab">
          <div class="deck-slab-depth">
            <div class="deck-chassis-back"></div>
            <div class="deck-chassis-slice"></div>
            <div class="deck-side deck-side-l"></div>
            <div class="deck-side deck-side-r"></div>
            <div class="deck-side deck-side-t"></div>
            <div class="deck-side deck-side-b"></div>
          </div>
          <div class="deck-card-face">
            <div class="deck-plate-pattern"></div>
            <div class="deck-card-bezel"></div>
            ${isIncomplete ? `
              <button class="deck-card-edit-btn deck-card-incomplete-badge" type="button" aria-label="Edit incomplete deck ${deck.name}" title="Incomplete deck — click to edit">
                <span class="deck-badge-icon">⚠️</span>
              </button>
            ` : `
              <button class="deck-card-edit-btn" type="button" aria-label="Edit ${deck.name}" title="Edit deck">
                <span class="deck-btn-pencil-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 20h9"></path>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                  </svg>
                </span>
              </button>
            `}
            ${crestHtml}
            <div class="deck-card-info">
              <div class="deck-card-name">${deck.name}</div>
              <div class="deck-card-meta">${totalCards} Cards</div>
            </div>
            <div class="deck-front-glass"></div>
          </div>
        </div>
      `;
      this.setupCardTilt(item);
      const terminateActiveMatchIfNeeded = () => {
        if (this.game?.isRunning) {
          this.game.terminateOngoingMatch?.();
          this.game.hubNavigator?.updateFloatingVoidButton?.();
        }
      };
      const goToEditor = () => {
        terminateActiveMatchIfNeeded();
        if (this.game?.deckBuilderModal) {
          this.game.deckBuilderModal.lastEditedDeckId = deck.id;
        }
        this.closeModal();
        this.game.deckBuilderModal?.open();
        this.game.deckBuilderModal?.openDeck(deck.id);
      };
      const activate = () => {
        if (isIncomplete) { goToEditor(); return; }
        this.selectDeck(deck.id, item);
      };
      item.addEventListener('click', activate);
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      });
      item.querySelector('.deck-card-edit-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        goToEditor();
      });
      container.appendChild(item);
    });

    // 3. "+" Empty deck slot — same silhouette as a real deck, drawn as a
    // 3D holographic wireframe docking bay waiting for a deck cartridge.
    const addItem = document.createElement('div');
    addItem.className = 'deck-select-card deck-add-card';
    addItem.setAttribute('role', 'button');
    addItem.setAttribute('tabindex', '0');
    addItem.title = 'Create New Deck';
    addItem.innerHTML = `
      <div class="deck-card-slab deck-add-slab">
        <div class="deck-slab-depth deck-add-depth">
          <div class="deck-chassis-back deck-add-back"></div>
          <div class="deck-side deck-side-l deck-add-side"></div>
          <div class="deck-side deck-side-r deck-add-side"></div>
          <div class="deck-side deck-side-t deck-add-side"></div>
          <div class="deck-side deck-side-b deck-add-side"></div>
        </div>
        <div class="deck-card-face deck-add-face">
          <div class="deck-add-wireframe-grid"></div>
          <span class="deck-add-plus">+</span>
          <span class="deck-add-label">New Deck</span>
        </div>
      </div>
    `;
    this.setupCardTilt(addItem);
    const createNew = () => {
      if (this.game?.isRunning) {
        this.game.terminateOngoingMatch?.();
        this.game.hubNavigator?.updateFloatingVoidButton?.();
      }
      this.closeModal();
      this.game.deckBuilderModal?.open();
      this.game.deckBuilderModal?.createNewDeck();
    };
    addItem.addEventListener('click', createNew);
    addItem.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        createNew();
      }
    });
    container.appendChild(addItem);
    this.updateScrollProgress();
    this._centeredDeckIdx = this.getCenteredDeckIndex();
    this._applyCenteredCardClass();
  }

  // Smooth 3D pointer-tracking tilt: responds to cursor position over the slab
  // with physical inertia and deflection, falling back to base straight resting pose.
  setupCardTilt(cardEl) {
    if (!cardEl || typeof cardEl.addEventListener !== 'function' || typeof cardEl.querySelector !== 'function') return;
    const slab = cardEl.querySelector('.deck-card-slab');
    if (!slab || !slab.style) return;

    let rect = null;
    let isTicking = false;
    let latestNormX = 0;
    let latestNormY = 0;

    const onPointerEnter = (e) => {
      if (e?.pointerType === 'touch') return;
      this.game?.audio?.playDeckHover?.();
      if (typeof cardEl.getBoundingClientRect === 'function') {
        rect = cardEl.getBoundingClientRect();
      }
    };

    const onPointerMove = (e) => {
      if (cardEl.classList?.contains?.('snapping')) return;
      if (e.pointerType === 'touch') return;
      if (!rect || !rect.width || !rect.height) {
        if (typeof cardEl.getBoundingClientRect === 'function') {
          rect = cardEl.getBoundingClientRect();
        }
        if (!rect || !rect.width || !rect.height) return;
      }

      latestNormX = (e.clientX - rect.left) / rect.width - 0.5;
      latestNormY = (e.clientY - rect.top) / rect.height - 0.5;

      if (!isTicking) {
        isTicking = true;
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => {
            isTicking = false;
            if (!rect) return; // Pointer already left
            const tiltY = latestNormX * 10;
            const tiltX = -latestNormY * 7;
            slab.style.transform = `perspective(1000px) translateY(-8px) rotateY(${tiltY.toFixed(2)}deg) rotateX(${tiltX.toFixed(2)}deg) scale(1.03) translateZ(12px)`;
          });
        }
      }
    };

    const onPointerLeave = () => {
      rect = null;
      isTicking = false;
      slab.style.transform = '';
    };

    cardEl.addEventListener('pointerenter', onPointerEnter);
    cardEl.addEventListener('pointermove', onPointerMove, { passive: true });
    cardEl.addEventListener('pointerleave', onPointerLeave);
  }

  // "Rastet ein" + glow: a quick, satisfying selection flash before the
  // deck actually starts the match, instead of an instant hard cut.
  selectAndPlay(cardEl, playFn) {
    if (this._selecting) return;
    this._selecting = true;

    this.modal?.querySelectorAll('.deck-select-card.active').forEach(el => el.classList.remove('active'));
    cardEl.classList.add('active', 'snapping');

    setTimeout(() => {
      this._selecting = false;
      this.closeModal();
      playFn();
    }, SELECT_ANIM_MS);
  }

  // ── Modal open/close ─────────────────────────────────────────────────
  open() {
    this.openModal();
  }

  openModal(opts = {}) {
    if (this.game?.isOnlineMatchLive?.()) {
      console.warn('[DeckBuilder] Archive cannot be opened during an active online match.');
      return;
    }
    const targetKey = opts?.selectDeckKey || this.game?.deckBuilderModal?.lastEditedDeckId || (this.isChallengeActive() ? this.getLastUsedDeckKey() : null);
    if (this.game?.deckBuilderModal) {
      this.game.deckBuilderModal.lastEditedDeckId = null;
    }
    this.ensureBayMounted();
    this.isOpen = true;
    this._selecting = false;
    this.game?.pauseForMenu?.('deckSelector');
    this.bayBg?.resume();
    // Preload thumbnails on demand when Archive / Deck Selection is explicitly opened
    preloadAllCardThumbnails();

    // Synchronize browser history for Android hardware back button / edge swipe
    if (typeof window !== 'undefined' && window.history?.pushState) {
      try {
        if (!opts?.fromHistory && !opts?.isMenuSwitch && window.history.state?.screen !== 'archive') {
          window.history.pushState({ screen: 'archive' }, '');
        }
      } catch (e) {}
    }
    // Sound-Batch-Pipeline Music Director (docs/SOUND_BATCH_PLAN.md section
    // 5): this modal is the game's one canonical "menu/hub" screen (reached
    // from every "return to menu" button, the campaign outro, and app
    // boot), so it's the single hook point for menu music. The hard-cut is
    // a no-op unless the player just came out of the campaign demo.
    this.game?.audio?.stopCampaignDemoMusicHardCut?.();
    this.game?.audio?.playMenuMusic?.();
    // Batch 4 (docs/SOUND_BATCH_PLAN.md section 4.4) - modal_open + die
    // Hangar-Ambience-Loop laufen über denselben Einstiegspunkt wie die
    // Menü-Musik oben.
    if (!opts?.isMenuSwitch) {
      this.game?.audio?.playModalOpen?.();
    }
    this.game?.audio?.playHangarAmbience?.();
    hubNavigator.setActiveDeck('archive');
    hubNavigator.updateFloatingVoidButton();
    if (this.modal) {
      this.modal.classList.add('active');
      this.updateDataIndicator();
      inboxManager.checkInbox().then(() => this.updateDataIndicator());
      this.clearDeckSelection();
      this.updateChallengeButtons();
      if (this._archiveMode === 'multiplayer') {
        this.modal.classList.add('archive-mode-multiplayer');
        this.updateOnlineSummary();
      } else {
        this.modal.classList.remove('archive-mode-multiplayer');
      }
      this._applyArchivePerfFlags();
      this._syncBayForMode();
      this.renderDecksList();
      this.updateScrollProgress();

      let didSelectTarget = false;
      if (targetKey) {
        const container = document.getElementById('deck-presets-list');
        const allCards = container
          ? (typeof container.querySelectorAll === 'function'
              ? Array.from(container.querySelectorAll('.deck-select-card'))
              : (container.children || []))
          : [];
        const targetCard = allCards.find(c => (c.dataset?.deckKey || c.getAttribute?.('data-deck-key')) === targetKey);
        if (targetCard) {
          this.selectDeck(targetKey, targetCard, { silent: true, instant: true });
          this.scrollToCard(targetCard, false);
          didSelectTarget = true;
        }
      }

      if (!didSelectTarget) {
        if (!this.isDesktop()) {
          this._initMobileDeckSelection();
        } else if (this._lastSelectedDeckKey) {
          this._restoreDesktopDeckSelection();
        }
      } else if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          if (this.isOpen && this._selectedCardEl) {
            this.scrollToCard(this._selectedCardEl, false);
          }
        });
      }
    }
  }

  toggleModal() {
    if (!this.isOpen && this.game?.isOnlineMatchLive?.()) {
      console.warn('[DeckBuilder] Archive cannot be opened during an active online match.');
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.modal) {
      if (typeof this.modal.classList?.toggle === 'function') {
        this.modal.classList.toggle('active', this.isOpen);
      } else if (this.modal.classList) {
        if (this.isOpen) this.modal.classList.add('active');
        else this.modal.classList.remove('active');
      }
      if (this.isOpen && this._archiveMode === 'multiplayer') {
        this.modal.classList.add('archive-mode-multiplayer');
        this.updateOnlineSummary();
      } else if (!this.isOpen) {
        this.modal.classList.remove('archive-mode-multiplayer');
      }
      if (this.isOpen) {
        this.ensureBayMounted();
        this.game?.pauseForMenu?.('deckSelector');
        this.bayBg?.resume();
        this._applyArchivePerfFlags();
        this._syncBayForMode();
        this.clearDeckSelection();
        this.renderDecksList();
        this.updateScrollProgress();
        if (!this.isDesktop()) {
          this._initMobileDeckSelection();
        } else if (this._lastSelectedDeckKey) {
          this._restoreDesktopDeckSelection();
        }
        hubNavigator.setActiveDeck('archive');
      } else {
        this.bayBg?.pause();
        this.clearDeckSelection();
        this.game?.resumeForMenu?.('deckSelector');
      }
      hubNavigator.updateFloatingVoidButton();
    }
  }

  closeModal(opts = {}) {
    const wasOpen = this.isOpen;
    this.isOpen = false;
    this._cardCenters = null;
    this._centeredDeckIdx = -1;
    if (this._swipeSelectTimer) {
      clearTimeout(this._swipeSelectTimer);
      this._swipeSelectTimer = null;
    }
    if (this._scrollSnapRestoreTimer) {
      clearTimeout(this._scrollSnapRestoreTimer);
      this._scrollSnapRestoreTimer = null;
    }
    this._isProgrammaticScrolling = false;
    this.bayBg?.pause();
    this.clearDeckSelection();

    // Clean up history state if closing outside of popstate
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      try {
        if (!opts?.fromHistory && !opts?.isMenuSwitch) {
          const currentScreen = window.history.state?.screen;
          if (currentScreen === 'archive' || currentScreen === 'archive_multiplayer') {
            const nextScreen = (this.game?.isRunning && !this.game?.isGameOver) ? 'game' : 'start';
            window.history.replaceState({ screen: nextScreen }, '');
          }
        }
      } catch (e) {}
    }

    // Batch 4 (docs/SOUND_BATCH_PLAN.md section 4.4).
    if (!opts?.isMenuSwitch) {
      this.game?.audio?.playModalClose?.();
    }
    if (this.modal) {
      this.modal.className = 'deck-select-modal-container';
    }
    hubNavigator.updateFloatingVoidButton();
    if (wasOpen) {
      this.game?.resumeForMenu?.('deckSelector');
    }
  }

  updateDataIndicator() {
    const dataIndicator = document.getElementById('archive-data-indicator');
    if (!dataIndicator) return;
    const hasNew = inboxManager.hasNewData();
    dataIndicator.classList.toggle('hidden', !hasNew);
  }

  isDesktop() {
    if (typeof window === 'undefined') return true;
    // Archive Deck Selector operates in two distinct layouts matching CSS media queries:
    // 1. Mobile Carousel: Phones in portrait (max-width: 768px) and phones in landscape (max-height: 500px).
    // 2. Desktop/Tablet Grid: Multi-column wrapping grid (all screens with width > 768px and height > 500px,
    //    including iPad, iPad Pro, unfolded Galaxy Z Fold, Android tablets, laptops and desktops).
    // In Grid mode, deck selection works via standard tap/click selection without horizontal scroll-snapping.
    if (window.matchMedia) {
      const isNarrowPhone = window.matchMedia('(max-width: 768px)').matches;
      const isLandscapePhone = window.matchMedia('(orientation: landscape) and (max-height: 560px)').matches ||
                               window.matchMedia('(orientation: landscape) and (max-height: 500px)').matches;
      if (isNarrowPhone || isLandscapePhone) return false;
      return true;
    }
    return true;
  }
}

